import React, { useState } from "react";
import { Card, Typography, Box, LinkButton, Button } from "@bca/ui-library";
import { ButtonVariants } from "@bca/ui-library/dist/Button/button.type";
import styled from "styled-components";
import { layout, space, flexbox } from "styled-system";
import { useSelector } from "react-redux";
import { useFeatureFlags } from "bca-feature-flag-provider";

import TryBetaButton from "./NewLiveBidding/TryBetaButton";
import SaleType from "./SaleType";
import CarSVG from "../Icons/CarSVG";
import ListOlSVG from "../Icons/ListOlSVG";
import MapMarkerSVG from "../Icons/MapMarkerSVG";
import joinSaleDataPush from "../../src/AdobeDataPushes/joinSaleDataPush";
import { buildPreviewLotsUrl } from "../ApiCalls/buildPreviewLotsUrl";
import { StoreType } from "../../redux/store.types";
import TermsAndConditionsModal from "./NewLiveBidding/TermsAndConditionsModal";
import tryNewDataPush from "../../src/AdobeDataPushes/tryNewDataPush";

import getSaleSectionsInfo from "../utils/getSaleSectionsInfo";

const CONFIRMED_TYPE_NAME = "Confirmed";
const SCHEDULE_TYPE_NAME = "Scheduled";
const SCHEDULE_TYPE_PREFIX = "Up to ";

const getSaleChannelLabel = (branchName: string): string => {
  switch (branchName) {
    case "Hybrid":
      return "Hybrid";
    case "Online Only":
    default:
      return "Online Auction";
  }
};

const StyledTypography = styled(Typography)<any>`
  ${layout}
`;

const FlexboxButton = styled(Button)<any>`
  ${flexbox}
`;

const BlueCard = styled(Card)<any>`
  border: solid 1px #a2c2da;
  background-color: rgba(33, 150, 243, 0.1);
  ${layout}
  ${space}
`;

export interface Sale {
  branchName: string;
  hallName: any;
  lotsAvailable: number;
  numberOfDistinctLocations: number;
  publishedSaleCode: string;
  publishedSaleName: string;
  saleDate: string;
  saleInformationType: string;
  saleStatus: string;
  sections: SaleSection[];
  isClosed: boolean;
}

export interface SaleSection {
  locations: string[];
  lotsAvailable: number;
  saleName: string;
  saleTypeCode: string;
}

interface ISaveInfoProps {
  sale: Sale;
  index: number;
  expand?: boolean;
}

export const getDistinctLocations = (sale: Sale) => {
  const locations = sale.sections.flatMap((section) => section.locations);
  const distinctLocations = locations.filter(
    (item: string, index: number) => locations.indexOf(item) === index
  );

  return distinctLocations;
};

export const getSaleDateParam = (saleDateUtc: string) => {
  const date = new Date(saleDateUtc);

  return date
    .toLocaleDateString("en-GB", {
      weekday: "short",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    .replace(/,/g, "")
    .replace(/\s/g, "+");
};

const SaleInfo: React.FC<ISaveInfoProps> = ({ sale, index, expand }) => {
  const [showSections, setShowSections] = useState<boolean>(expand);
  const [showModal, setShowModal] = useState(false);

  const {
    featureFlags: { closedSalesRedirect = false },
    isLoading: isLoadingFeatureFlags,
  } = useFeatureFlags();

  const handleOpenModal = () => {
    setShowModal(true);
    tryNewDataPush();
  };

  const handleCloseModal = () => setShowModal(false);

  const store = useSelector((state: StoreType) => ({
    isVendor: state.app.isVendor,
    isLolVendor: state.app.isLolVendor,
  }));

  const getSaleInformationType = (saleInfoType, saleStatus) => {
    return saleInfoType === CONFIRMED_TYPE_NAME && saleStatus === "Started"
      ? "Started"
      : saleInfoType;
  };

  const getSaleName = (sections: SaleSection[]) =>
    sections.map((section) => section.saleName);

  const saleDate = `${new Date(sale.saleDate).toLocaleTimeString("en-GB", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  })} ${new Date(sale.saleDate).toLocaleString("en-GB", {
    weekday: "long",
  })}`;

  const saleIsToday = () => {
    const saleDate = new Date(sale.saleDate);
    saleDate.setUTCHours(0, 0, 0, 0);

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const isSameDay = saleDate.getDate() === today.getDate();
    const isSameMonth = saleDate.getMonth() === today.getMonth();
    const isSameYear = saleDate.getFullYear() === today.getFullYear();

    return isSameDay && isSameMonth && isSameYear;
  };

  const joinSaleAnalyticsHandler = () => {
    if (saleIsToday()) {
      joinSaleDataPush();
    }
  };

  const saleButtonLabel =
    store.isVendor && !closedSalesRedirect && !store.isLolVendor
      ? "Classic View"
      : "Join Live Sale";

  const saleLocation = getDistinctLocations(sale).join(",");
  const saleType = getSaleName(sale.sections).join(",");
  const saleDateParam = getSaleDateParam(sale.saleDate);

  const isPrivateSale = sale.isClosed;

  const oldBiddingPageUrl = `${process.env.liveOnlineUrl}deepLink?catalogueRef=${sale.publishedSaleCode}`;

  const getLinkButton = () => {
    const commonProps = {
      variant: "primary-gradient" as ButtonVariants,
      minWidth: ["calc(100% - 70px)", 160, 200, 200],
      paddingX: [8, 9],
      marginRight: "18px",
      display: [saleIsToday() ? "none" : "inline-flex", "inline-flex"],
      shouldOpenInNewWindow: saleIsToday() ? true : false,
    };

    if (saleIsToday()) {
      if (
        !(store.isVendor || store.isLolVendor) &&
        (!isPrivateSale || closedSalesRedirect)
      ) {
        return (
          <LinkButton
            data-testid="today-live-sale-button"
            onClick={handleOpenModal}
            {...commonProps}
          >
            Join Live Sale
          </LinkButton>
        );
      }

      if (store.isVendor && !store.isLolVendor && closedSalesRedirect) {
        return (
          <LinkButton
            data-testid="today-live-sale-button"
            onClick={handleOpenModal}
            {...commonProps}
          >
            Join Live Sale
          </LinkButton>
        );
      }

      if (sale.saleInformationType !== CONFIRMED_TYPE_NAME) return null;
      return (
        <LinkButton
          data-testid="today-classic-view-button"
          link={oldBiddingPageUrl}
          onClick={joinSaleAnalyticsHandler}
          {...commonProps}
        >
          {saleButtonLabel}
        </LinkButton>
      );
    }

    if (isPrivateSale && !closedSalesRedirect) {
      if (sale.saleInformationType !== CONFIRMED_TYPE_NAME) return null;
      return (
        <LinkButton
          data-testid="preview-lots-classic-view"
          link={oldBiddingPageUrl}
          onClick={joinSaleAnalyticsHandler}
          {...commonProps}
        >
          Preview Lots
        </LinkButton>
      );
    }

    if (isPrivateSale && closedSalesRedirect) {
      if (sale.saleInformationType !== CONFIRMED_TYPE_NAME) return null;

      if (store.isLolVendor) {
        return (
          <LinkButton
            data-testid="preview-lots-classic-view"
            link={oldBiddingPageUrl}
            onClick={joinSaleAnalyticsHandler}
            {...commonProps}
          >
            Preview Lots
          </LinkButton>
        );
      }

      return (
        <LinkButton
          data-testid="preview-live-sale-button"
          onClick={handleOpenModal}
          {...commonProps}
        >
          Join Live Sale
        </LinkButton>
      );
    }

    if (store.isLolVendor) {
      if (sale.saleInformationType !== CONFIRMED_TYPE_NAME) return null;
      return (
        <LinkButton
          data-testid="preview-lots-classic-view"
          link={oldBiddingPageUrl}
          onClick={joinSaleAnalyticsHandler}
          {...commonProps}
        >
          Preview Lots
        </LinkButton>
      );
    }

    return (
      <LinkButton
        data-testid="preview-lots-with-filters"
        link={buildPreviewLotsUrl(saleLocation, saleType, saleDateParam, sale.branchName)}
        {...commonProps}
      >
        Preview Lots
      </LinkButton>
    );
  };

  const getSaleLinkComponent = () => {
    if (sale.lotsAvailable === 0 || !saleIsToday()) {
      return (
        <Typography
          variant="body"
          paddingRight="30px"
          margin={0}
          color="footer-blue"
          css="font-weight: bold; font-size: 16px;"
          data-testid="sale-view-lots-text"
        >
          {sale.saleInformationType === SCHEDULE_TYPE_NAME
            ? SCHEDULE_TYPE_PREFIX
            : ""}
          {sale.lotsAvailable} Lots
        </Typography>
      );
    } else {
      if (store.isLolVendor) {
        if (sale.saleInformationType === CONFIRMED_TYPE_NAME) {
          return (
            <LinkButton
              variant="tertiary"
              width="auto"
              paddingX={0}
              paddingRight="30px"
              height="19px"
              fontWeight="bold"
              fontSize="16px"
              data-testid="sale-view-lots-link"
              link={oldBiddingPageUrl}
              shouldOpenInNewWindow={false}
            >
              {sale.lotsAvailable} Lots
            </LinkButton>
          );
        } else {
          return (
            <Typography
              variant="body"
              paddingRight="30px"
              margin={0}
              color="footer-blue"
              css="font-weight: bold; font-size: 16px;"
              data-testid="sale-view-lots-text"
            >
              {sale.saleInformationType === SCHEDULE_TYPE_NAME
                ? SCHEDULE_TYPE_PREFIX
                : ""}
              {sale.lotsAvailable} Lots
            </Typography>
          );
        }
      } else {
        if (isPrivateSale) {
          if (sale.saleInformationType === CONFIRMED_TYPE_NAME) {
            if (closedSalesRedirect) {
              return (
                <LinkButton
                  variant="tertiary"
                  width="auto"
                  paddingX={0}
                  paddingRight="30px"
                  height="19px"
                  fontWeight="bold"
                  fontSize="16px"
                  data-testid="sale-view-lots-link"
                  onClick={handleOpenModal}
                  shouldOpenInNewWindow={false}
                >
                  {sale.lotsAvailable} Lots
                </LinkButton>
              );
            } else {
              return (
                <LinkButton
                  variant="tertiary"
                  width="auto"
                  paddingX={0}
                  paddingRight="30px"
                  height="19px"
                  fontWeight="bold"
                  fontSize="16px"
                  data-testid="sale-view-lots-link"
                  link={oldBiddingPageUrl}
                  shouldOpenInNewWindow={false}
                >
                  {sale.lotsAvailable} Lots
                </LinkButton>
              );
            }
          } else {
            return (
              <Typography
                variant="body"
                paddingRight="30px"
                margin={0}
                color="footer-blue"
                css="font-weight: bold; font-size: 16px;"
                data-testid="sale-view-lots-text"
              >
                {sale.saleInformationType === SCHEDULE_TYPE_NAME
                  ? SCHEDULE_TYPE_PREFIX
                  : ""}
                {sale.lotsAvailable} Lots
              </Typography>
            );
          }
        } else {
          return (
            <LinkButton
              variant="tertiary"
              width="auto"
              paddingX={0}
              paddingRight="30px"
              height="19px"
              fontWeight="bold"
              fontSize="16px"
              data-testid="sale-view-lots-link"
              link={buildPreviewLotsUrl(
                getSaleSectionsInfo(sale).locations,
                getSaleSectionsInfo(sale).saleNames,
                saleDateParam,
                sale.branchName
              )}
              shouldOpenInNewWindow={false}
            >
              {sale.lotsAvailable} Lots
            </LinkButton>
          );
        }
      }
    }
  };

  const getSaleSectionLinkComponent = (section: SaleSection) => {
    if (store.isLolVendor) {
      if (sale.saleInformationType === CONFIRMED_TYPE_NAME) {
        return (
          <LinkButton
            variant="tertiary"
            width="auto"
            paddingX={0}
            height="19px"
            fontSize="16px"
            marginTop="15px"
            minWidth="100px"
            data-testid="location-view-lots-link"
            link={oldBiddingPageUrl}
            shouldOpenInNewWindow={false}
          >
            {section.lotsAvailable} lots
          </LinkButton>
        );
      } else {
        return (
          <StyledTypography
            variant="body"
            color="#64798c"
            paddingX={0}
            height="19px"
            fontSize="16px"
            marginTop="15px"
            minWidth="100px"
            data-testid="location-view-lots-text"
          >
            {sale.saleInformationType === SCHEDULE_TYPE_NAME
              ? SCHEDULE_TYPE_PREFIX
              : ""}
            {section.lotsAvailable} lots
          </StyledTypography>
        );
      }
    } else {
      if (isPrivateSale) {
        if (sale.saleInformationType === CONFIRMED_TYPE_NAME) {
          if (closedSalesRedirect) {
            return (
              <LinkButton
                variant="tertiary"
                width="auto"
                paddingX={0}
                height="19px"
                fontSize="16px"
                marginTop="15px"
                minWidth="100px"
                data-testid="location-view-lots-link"
                onClick={handleOpenModal}
                shouldOpenInNewWindow={false}
              >
                {section.lotsAvailable} lots
              </LinkButton>
            );
          } else {
            return (
              <LinkButton
                variant="tertiary"
                width="auto"
                paddingX={0}
                height="19px"
                fontSize="16px"
                marginTop="15px"
                minWidth="100px"
                data-testid="location-view-lots-link"
                link={oldBiddingPageUrl}
                shouldOpenInNewWindow={false}
              >
                {section.lotsAvailable} lots
              </LinkButton>
            );
          }
        } else {
          return (
            <StyledTypography
              variant="body"
              color="#64798c"
              paddingX={0}
              height="19px"
              fontSize="16px"
              marginTop="15px"
              minWidth="100px"
              data-testid="location-view-lots-text"
            >
              {sale.saleInformationType === SCHEDULE_TYPE_NAME
                ? SCHEDULE_TYPE_PREFIX
                : ""}
              {section.lotsAvailable} lots
            </StyledTypography>
          );
        }
      } else {
        return (
          <LinkButton
            variant="tertiary"
            width="auto"
            paddingX={0}
            height="19px"
            fontSize="16px"
            marginTop="15px"
            minWidth="100px"
            data-testid="location-view-lots-link"
            link={buildPreviewLotsUrl(
              section.locations.join(","),
              section.saleName,
              saleDateParam,
              sale.branchName
            )}
            shouldOpenInNewWindow={false}
          >
            {section.lotsAvailable} lots
          </LinkButton>
        );
      }
    }
  };

  return (
    !isLoadingFeatureFlags && (
      <Card
        height="auto"
        width="100%"
        paddingX={["24px", "36px"]}
        paddingY={["24px", "36px"]}
        marginX={0}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="row"
          flexGrow={1}
        >
          <Typography variant="h4" marginTop={0} marginBottom="12px">
            {sale.publishedSaleName}
          </Typography>
          {saleIsToday() &&
            !isPrivateSale &&
            !closedSalesRedirect &&
            !store.isLolVendor && (
              <TryBetaButton handleOpenModal={handleOpenModal} />
            )}
        </Box>
        <Box
          id={`id_${sale.publishedSaleCode}`}
          display="flex"
          flexDirection={["column", "row"]}
          alignItems={["start", "center"]}
        >
          <SaleType
            saleInformationType={getSaleInformationType(
              sale.saleInformationType,
              sale.saleStatus
            )}
          ></SaleType>
          {getSaleInformationType(sale.saleInformationType, sale.saleStatus) ===
            SCHEDULE_TYPE_NAME && (
            <Typography
              variant="body"
              color="#64798c"
              css="font-size:15px;"
              margin={0}
            >
              Time and Sale sections subject to change
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="flex-end" flexWrap="wrap">
          <Box flex="1" display="flex" flexDirection="column">
            <Box display="flex">
              <Typography
                variant="body"
                paddingRight="12px"
                marginRight="12px"
                marginBottom={0}
                marginTop="16px"
                color="#64798c"
                css={`
                  font-size: 15px;
                  position: relative;
                  &:after {
                    content: "";
                    height: 15px;
                    width: 2px;
                    background-color: #7e9ab3;
                    opacity: 0.2;
                    position: absolute;
                    right: 0;
                    top: 2px;
                    border-radius: 1px;
                  }
                `}
              >
                {saleDate}
              </Typography>
              <Typography
                variant="body"
                marginBottom={0}
                marginTop="16px"
                color="#64798c"
                css="font-size:15px;"
              >
                {getSaleChannelLabel(sale.branchName)}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="start"
              marginBottom={["24px", 0]}
            >
              <Box display="flex" alignItems="center" marginTop="16px">
                <CarSVG
                  fill="#060916"
                  width="18px"
                  marginRight="12px"
                  paddingBottom="2px"
                ></CarSVG>

                {getSaleLinkComponent()}
              </Box>
              <Box display="flex" alignItems="center" marginTop="16px">
                <ListOlSVG
                  fill="#060916"
                  width="18px"
                  marginRight="12px"
                  paddingBottom="2px"
                ></ListOlSVG>
                <Typography
                  variant="body"
                  paddingRight="36px"
                  margin={0}
                  color="footer-blue"
                  css="font-weight: bold; font-size: 16px; min-width: 134px;"
                >
                  {sale.sections.length} Sale Sections
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" marginTop="16px">
                <MapMarkerSVG
                  fill="#060916"
                  width="18px"
                  marginRight="12px"
                  paddingBottom="2px"
                ></MapMarkerSVG>
                <Typography
                  variant="body"
                  margin={0}
                  color="footer-blue"
                  css="font-weight: bold; font-size: 16px; min-width: 134px;"
                >
                  {sale.numberOfDistinctLocations} Locations
                </Typography>
              </Box>
            </Box>
          </Box>
          {getLinkButton()}
          {saleIsToday() && (
            <BlueCard
              paddingX={6}
              paddingY={5}
              marginX={0}
              marginY={0}
              mr={6}
              width="calc(100% - 76px)"
              height="auto"
              display={["inline-flex", "none"]}
            >
              <Typography
                variant="body"
                margin={0}
                color="#6f8497"
                css="font-size:15px;"
              >
                To participate in this sale on your mobile, please use the{" "}
                <LinkButton
                  variant="tertiary"
                  padding={0}
                  height={20}
                  css="font-size:15px;"
                  link="https://www.bca.co.uk/buy/buyer-app"
                >
                  Buyer App
                </LinkButton>
              </Typography>
            </BlueCard>
          )}
          <FlexboxButton
            variant="tertiary"
            width="auto"
            paddingX={12}
            paddingTop="2px"
            alignSelf={["center", "flex-end"]}
            aria-expanded={showSections ? "true" : "false"}
            aria-controls={`${sale.publishedSaleCode}${index}`}
            onClick={() => {
              setShowSections((prevState) => !prevState);
            }}
            css={`
              background: white;
              box-shadow: 0 2px 10px 0 rgba(217, 217, 217, 0.5);
              color: red;
              cursor: pointer;
              &:not([disabled]):hover {
                background: white;
                box-shadow: 0 2px 10px 0 rgba(217, 217, 217, 0.5);
              }
            `}
            data-testid="expand-sections-btn"
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 284.929 284.929"
              fill="url(#arrowgrad) #2196f3"
              style={{
                transform: `rotate(${showSections ? "-180deg" : "0deg"})`,
                transition: "all 0.2s ease-in-out",
              }}
            >
              <path
                d="M282.082,76.511l-14.274-14.273c-1.902-1.906-4.093-2.856-6.57-2.856c-2.471,0-4.661,0.95-6.563,2.856L142.466,174.441
                      L30.262,62.241c-1.903-1.906-4.093-2.856-6.567-2.856c-2.475,0-4.665,0.95-6.567,2.856L2.856,76.515C0.95,78.417,0,80.607,0,83.082
                      c0,2.473,0.953,4.663,2.856,6.565l133.043,133.046c1.902,1.903,4.093,2.854,6.567,2.854s4.661-0.951,6.562-2.854L282.082,89.647
                      c1.902-1.903,2.847-4.093,2.847-6.565C284.929,80.607,283.984,78.417,282.082,76.511z"
              ></path>
            </svg>
          </FlexboxButton>
        </Box>
        <Box
          hidden={showSections ? false : true}
          id={`${sale.publishedSaleCode}${index}`}
          style={{ borderTop: "1px dashed rgba(151, 151, 151, 0.15)" }}
          marginTop="36px"
          paddingTop="12px"
        >
          {sale.sections.map((section, index) => (
            <Box
              display="flex"
              justifyContent="start"
              key={section.saleName + index}
            >
              <Box
                display="flex"
                flex="1"
                flexDirection={["column", "row"]}
                alignItems="baseline"
              >
                <StyledTypography
                  variant="body"
                  color="#footer-blue"
                  marginBottom={0}
                  marginRight="10px"
                  marginTop="15px"
                  width={["auto", "30%"]}
                  css={`
                    font-size: 16px;
                    font-weight: 500;
                    line-height: 19px;
                  `}
                >
                  {section.saleName}
                </StyledTypography>
                <Typography
                  variant="body"
                  color="#64798c"
                  marginRight="20px"
                  marginBottom={0}
                  marginTop="15px"
                  css={`
                    flex: 2;
                    font-size: 15px;
                    line-height: 19px;
                  `}
                >
                  {section.locations.join(", ")}
                </Typography>
              </Box>
              {getSaleSectionLinkComponent(section)}
            </Box>
          ))}
        </Box>
        {showModal && (
          <TermsAndConditionsModal
            handleCloseModal={handleCloseModal}
            catalogueRef={sale.publishedSaleCode}
          />
        )}
      </Card>
    )
  );
};

export default SaleInfo;
